import Dexie, { Table } from 'dexie';

export type QueueStatus = 'pending' | 'syncing' | 'failed' | 'done';
export type ActionType =
  | 'attendance_checkin'
  | 'attendance_break_out'
  | 'attendance_break_in'
  | 'attendance_checkout'
  | 'task_report_submit'
  | 'task_pending'
  | 'task_start'
  | 'feedback_comment';

export interface QueueItem {
  id: string;           // UUID
  user_id: string;
  action_type: ActionType;
  payload: Record<string, any>;
  client_timestamp: string; // ISO string — waktu aksi dilakukan di device
  created_at: string;
  retry_count: number;
  status: QueueStatus;
  error?: string;
}

class OfflineQueueDB extends Dexie {
  queue!: Table<QueueItem, string>;

  constructor() {
    super('teratask_offline_queue');
    this.version(1).stores({
      queue: 'id, user_id, action_type, status, created_at',
    });
  }
}

const db = new OfflineQueueDB();
const MAX_QUEUE_SIZE = 30;
export const MAX_RETRY = 3;

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const offlineQueue = {
  async add(
    item: Pick<QueueItem, 'user_id' | 'action_type' | 'payload'>
  ): Promise<string> {
    const pending = await this.getPendingCount();
    if (pending >= MAX_QUEUE_SIZE) {
      throw new Error(`Antrian offline penuh (maks. ${MAX_QUEUE_SIZE} item). Hubungkan ke internet untuk mengirim data tertunda terlebih dahulu.`);
    }
    const now = new Date().toISOString();
    const id = generateId();
    await db.queue.add({
      id,
      ...item,
      client_timestamp: now,
      created_at: now,
      retry_count: 0,
      status: 'pending',
    });
    return id;
  },

  async getPending(): Promise<QueueItem[]> {
    return db.queue
      .where('status')
      .anyOf(['pending', 'failed'])
      .sortBy('created_at');
  },

  async getPendingCount(): Promise<number> {
    return db.queue.where('status').anyOf(['pending', 'failed']).count();
  },

  async markSyncing(id: string): Promise<void> {
    await db.queue.update(id, { status: 'syncing' });
  },

  async markDone(id: string): Promise<void> {
    await db.queue.update(id, { status: 'done' });
  },

  async markFailed(id: string, error: string): Promise<void> {
    await db.queue.where('id').equals(id).modify((item) => {
      item.status = 'failed';
      item.error = error;
      item.retry_count += 1;
    });
  },

  async clearDone(): Promise<void> {
    await db.queue.where('status').equals('done').delete();
  },

  async clearFailed(): Promise<void> {
    await db.queue.where('status').equals('failed').delete();
  },

  async getAll(): Promise<QueueItem[]> {
    return db.queue.orderBy('created_at').toArray();
  },

  async getPermanentlyFailedCount(): Promise<number> {
    const failed = await db.queue.where('status').equals('failed').toArray();
    return failed.filter(item => item.retry_count >= MAX_RETRY).length;
  },
};
